import { useMutation as _useMutation, useQuery as _useQuery } from "@apollo/client";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { logger as _logger } from "@clockwise/client-commons/src/util/logger";
import { ResponseStatusEnum } from "@clockwise/schema";
import { apolloErrorLogger } from "@clockwise/web-commons/src/util/apolloErrorLogger";
import { find, isEmpty, isEqual } from "lodash";
import {
  UpdateEventResponseStatusDocument,
  UpdateEventResponseStatusMutation,
} from "../../event/graphql/__generated__/EventRenderer.generated";
import { useOrgId as _useOrgId } from "../../hooks/useOrgId";
import { getAttendeeResponseStatusFromEventParent } from "../../web-app-calendar/util/getAttendeeResponseStatusFromEventParent";
import { EventAttendeesDocument } from "../graphql/__generated__/EventAttendees.generated";

type DependencyInjectionOptions = {
  logger?: typeof _logger;
  useQuery?: typeof _useQuery;
  useMutation?: typeof _useMutation;
  useOrgId?: typeof _useOrgId;
};

type Response = {
  responseStatus: ResponseStatusEnum | undefined;
  error: Error | undefined;
  loading: boolean;
  update?: (options: {
    variables: { responseStatus: ResponseStatusEnum };
    onCompleted?: () => void;
    onError?: () => void;
  }) => Promise<void>;
  updating: boolean;
  updateError: Error | undefined;
};

type ResponseStatusEvent = {
  __typename: "Event";
  id: string;
  eventKey: {
    __typename: "EventKey";
    id: string;
    calendarIds: string[] | null;
    externalEventId: string;
  };
  attendees: {
    id: string;
    __typename: "Attendee";
    responseStatus: ResponseStatusEnum | null;
    urnValue: string;
  }[];
};

const defaultResponse: Response = {
  responseStatus: undefined,
  error: undefined,
  loading: true,
  update: undefined,
  updating: false,
  updateError: undefined,
};

const getOptimisticResponse = ({
  event,
  eventParentId = "",
  updateCalendarId = "",
  updateResponseStatus,
}: {
  event?: ResponseStatusEvent;
  eventParentId: string;
  updateCalendarId: string;
  updateResponseStatus?: ResponseStatusEnum;
}): UpdateEventResponseStatusMutation => ({
  updateEventResponseStatus: {
    clientMutationId: null,
    eventParent: {
      id: eventParentId,
      events: event
        ? [
            {
              id: event.id,
              eventKey: event.eventKey,
              attendees:
                event.attendees.map((attendee) => ({
                  ...attendee,
                  responseStatus:
                    attendee.urnValue === updateCalendarId && !!updateResponseStatus
                      ? updateResponseStatus
                      : attendee.responseStatus,
                })) || [],
              __typename: "Event",
            },
          ]
        : [],
      __typename: "EventParent",
    },
    __typename: "UpdateEventResponseStatusMutationPayload",
  },
  __typename: "Mutation",
});

export const useEventResponseStatus = (
  externalEventId: string,
  calendarId: string,
  options?: DependencyInjectionOptions,
) => {
  const logger = options?.logger ?? _logger;
  const useQuery = options?.useQuery ?? _useQuery;
  const useMutation = options?.useMutation ?? _useMutation;
  const useOrgId = options?.useOrgId ?? _useOrgId;

  const { orgId } = useOrgId();

  const doSkip = !orgId || isEmpty(externalEventId) || isEmpty(calendarId);

  const { data, error, loading } = useQuery(EventAttendeesDocument, {
    skip: doSkip,
    variables: {
      // Always defined because we skip when we do not have an org.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
      orgId: orgId!,
      externalEventId,
      calendarId: calendarId,
    },
    // DevNote: We can't use the cache because there's two bugs that occur when we use the cache here:
    // 1. `Attendee` entities are not being updated in the cache properly because they lack `id` fields.
    // Probably the id field should be combination of `urnValue`(calendarId) and `externalEventId`
    // 2. The cache drops the `responseStatus` field of the `Attendee` sometimes! This is a bug in Apollo Client.
    // There's not even an `null` value, it's just a missing field, though it's clearly specified in `EventAttendeesDocument`
    // When we use the cache, the UI flickers whenever `responseStatus` is dropped
    fetchPolicy: "no-cache",
  });

  const [mutate, { loading: updating, error: updateError }] = useMutation(
    UpdateEventResponseStatusDocument,
  );

  if (doSkip) {
    return defaultResponse;
  }

  if (error) {
    apolloErrorLogger({
      errorMessagePrefix: "Error fetching event details",
      error,
      errorContext: {
        externalEventId,
        calendarId,
      },
    });
    return defaultResponse;
  }

  if (!loading && !data) {
    logger.error("unabled to fetch event details for event", {
      error: "no data",
      externalEventId,
      calendarId,
    });
    return defaultResponse;
  }

  const dataOrg = getValue(data?.org, "Org");
  const eventParent = getValue(dataOrg?.forceFetchEventParentErrorable);
  const events = eventParent?.events;
  const event = find(events, (event) => event?.eventKey?.externalEventId === externalEventId);
  const responseStatus = getAttendeeResponseStatusFromEventParent(
    eventParent,
    externalEventId,
    calendarId,
  );

  const update = async ({
    variables: { responseStatus },
    onCompleted,
    onError,
  }: {
    variables: { responseStatus: ResponseStatusEnum };
    onCompleted?: () => void;
    onError?: () => void;
  }) => {
    await mutate({
      variables: {
        input: {
          calendarId: calendarId,
          responseStatus,
          orgRelayId: orgId || "",
          externalEventId,
        },
      },
      optimisticResponse: getOptimisticResponse({
        event,
        eventParentId: eventParent?.id || "",
        updateCalendarId: calendarId,
        updateResponseStatus: responseStatus,
      }),
      onCompleted: (mutationData) => {
        const mutationResponseStatus = getAttendeeResponseStatusFromEventParent(
          mutationData?.updateEventResponseStatus?.eventParent,
          externalEventId,
          calendarId,
        );

        if (isEqual(mutationResponseStatus, responseStatus)) {
          onCompleted?.();
        } else {
          onError?.();
        }
      },
      onError,
    });
  };

  return {
    ...defaultResponse,
    responseStatus,
    error,
    loading,
    update,
    updating,
    updateError,
  };
};

export default useEventResponseStatus;
